import type { ChangeDefinition, CommonImportParameters } from '@sap/ux-specification-types';
import { v2, ManifestSection, PageTypeV2, MANIFESTPATH, LogSeverity } from '@sap/ux-specification-types';
import { ensureManifestSectionByPathV2 } from '../export/manifest';
import { getPageTypeV2, parseChangeString } from '../../common';
import i18next from 'i18next';
import type { ExtensionLogger } from '../../..';
import { log } from '../../../extensionLogger';

/**
 * Determines a V2 object page in the manifest, based on the given entity set.
 *
 * @param v2Pages - manifest['sap.ui.generic.app'].pages
 * @param entitySet - current entityset
 * @param pageKeys - list of page keys, to be filled
 * @returns v2Page - page as specified in the manifest
 */
export function findObjectPageV2(
    v2Pages: v2.SapUiAppPagesV2,
    entitySet: string,
    pageKeys: string[]
): v2.SapUiAppPageV2 | undefined {
    let foundPage;
    for (const key in v2Pages) {
        const v2Page = v2Pages[key];
        if (getPageTypeV2(v2Page.component?.name) === PageTypeV2.ObjectPage && v2Page.entitySet === entitySet) {
            pageKeys.unshift(key);
            return v2Page;
        } else if (v2Page.pages) {
            foundPage = findObjectPageV2(v2Page.pages, entitySet, pageKeys);
            if (foundPage) {
                pageKeys.unshift(key);
                return foundPage;
            }
        }
    }
}

/**
 * Determines a V2 list report page in the manifest.
 *
 * @param v2Pages - manifest['sap.ui.generic.app'].pages
 * @param pageKeys - list of page keys, to be filled
 * @returns v2Page - page as specified in the manifest
 */
export function findListReportPageV2(v2Pages: v2.SapUiAppPagesV2, pageKeys: string[]): v2.SapUiAppPageV2 | undefined {
    for (const key in v2Pages) {
        const v2Page = v2Pages[key];
        if (getPageTypeV2(v2Page.component?.name) === PageTypeV2.ListReport) {
            pageKeys.push(key);
            return v2Page;
        }
    }
}

/**
 * Validate the given page definition from manifest.
 *
 * @param importParameters  - object comprising all API input data
 * @returns {v2.SapUiAppPagesV2 | undefined} Pages definition in manifest if found
 */
export function getV2Pages(importParameters: CommonImportParameters): v2.SapUiAppPagesV2 | undefined {
    if (!importParameters.manifest[ManifestSection.generic]) {
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOFE'),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.generic]
            }
        });
        return;
    }
    const v2Pages = importParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2;
    if (!v2Pages) {
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOPAGES', { appId: importParameters.manifest['sap.app']['id'] }),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.generic]
            }
        });
        return;
    }
    return v2Pages;
}

/**
 * Validate the given page definition from manifest.
 *
 * @param v2Page - manifest page
 * @param logger - logger for writing the check results
 * @param entitySet - the given entiset from the API call
 */
export function checkV2Page(v2Page: v2.SapUiAppPageV2, logger: ExtensionLogger, entitySet: string): void {
    if (!v2Page) {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOPAGEFORENTITYSET', { entitySet }),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.generic]
            }
        });
        return;
    }
    if (!v2Page.entitySet) {
        const pageName = v2Page.component?.name;
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOENTITYSET', { pageName }),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.generic]
            }
        });
    }
}

/**
 * Determines a V2 analytical list page in the manifest.
 *
 * @param v2Pages - manifest['sap.ui.generic.app'].pages
 * @param pageKeys - list of page keys, to be filled
 * @param logger - logger for writing error messages if the analytical list page is not found
 * @returns v2Page - page as specified in the manifest
 */
export function findAnalyticalListPage(
    v2Pages: v2.SapUiAppPagesV2,
    pageKeys: string[],
    logger?: ExtensionLogger
): v2.SapUiAppPageV2 | undefined {
    for (const key in v2Pages) {
        const v2Page = v2Pages[key];
        pageKeys.push(key);
        if (getPageTypeV2(v2Page.component?.name) === PageTypeV2.AnalyticalListPage) {
            return v2Page;
        }
    }
    //Not found ==> log
    log(logger, {
        severity: LogSeverity.Error,
        message: i18next.t('NOALP'),
        location: {
            path: MANIFESTPATH,
            range: [ManifestSection.generic]
        }
    });
}

/**
 * Method checks if passed extension key is associated to custom section with passed entity.
 *
 * @param {string} extensionKey - Extension key.
 * @param {string} entity - Entity to check in extension.
 * @returns {boolean} Is extension key relates to custom section and associated to passed entity.
 */
export function isAssociatedCustomSection(extensionKey: string, entity: string): boolean {
    const keyParts = extensionKey.split('|');
    return v2.SectionPosition[keyParts[0]] && keyParts[1] === entity;
}

/**
 * Method which finds custom section extensions in manifest for passed entitySet.
 *
 * @param manifest - manifest.
 * @param entitySet - Entity set to lookup while searching custom section extensions.
 * @returns {object} Found object with custom sections
 */
export function findCustomSectionExtensions(manifest: object, entitySet: string): object {
    const customSectionViews = ensureManifestSectionByPathV2(
        manifest,
        `sap.ui5/extends/extensions/${v2.SAPUI5_VIEW_EXTENSION}/${v2.SAPUI5_VIEW_EXTENSION_OBJECT_PAGE}`
    );
    const pageCustomSections = {};
    for (const key in customSectionViews) {
        if (isAssociatedCustomSection(key, entitySet)) {
            pageCustomSections[key] = customSectionViews[key];
        }
    }
    return pageCustomSections;
}

/**
 * Removes empty properties of Footer.
 *
 * @param config - the configuration to be checked
 */
function removeEmptySettingsOfFooter(config: v2.AnalyticalListPageConfigV2 | v2.ListReportConfigV2) {
    if (config.footer?.actions) {
        for (const actionKey in config.footer.actions) {
            if (Object.keys(config.footer.actions[actionKey]).length === 0) {
                delete config.footer.actions[actionKey];
            }
        }
        if (Object.keys(config.footer.actions).length === 0) {
            delete config.footer.actions;
        }
        if (Object.keys(config.footer).length === 0) {
            delete config.footer;
        }
    }
}

/**
 * Removes empty properties of FilterBar.
 *
 * @param config - the configuration to be checked
 */
function removeEmptySettingsOfFilterBar(config: v2.AnalyticalListPageConfigV2 | v2.ListReportConfigV2) {
    if (config.filterBar?.selectionFields) {
        for (const fieldKey in config.filterBar.selectionFields) {
            if (Object.keys(config.filterBar.selectionFields[fieldKey]).length === 0) {
                delete config.filterBar.selectionFields[fieldKey];
            }
        }
        if (Object.keys(config.filterBar?.selectionFields)?.length === 0) {
            delete config.filterBar.selectionFields;
        }
    }
}

/**
 * Removes empty properties of ToolBar.
 *
 * @param config - the configuration to be checked
 */
function removeEmptySettingsOfToolBar(config: v2.AnalyticalListPageConfigV2 | v2.ListReportConfigV2) {
    if (config.table?.toolBar) {
        for (const actionKey in config.table.toolBar.actions) {
            if (Object.keys(config.table.toolBar.actions[actionKey]).length === 0) {
                delete config.table.toolBar.actions[actionKey];
            }
        }
        if (Object.keys(config.table.toolBar.actions).length === 0) {
            delete config.table.toolBar.actions;
        }
        if (Object.keys(config.table.toolBar).length === 0) {
            delete config.table.toolBar;
        }
    }
}

/**
 * Removes empty properties in the config.json of Analytical List Page V2 | ListReport Page V2.
 *
 * @param config - the configuration to be checked
 */
export function removeEmptySettings(config: v2.AnalyticalListPageConfigV2 | v2.ListReportConfigV2): void {
    // Check ToolBar
    removeEmptySettingsOfToolBar(config);
    // Check FilterBar
    removeEmptySettingsOfFilterBar(config);
    // Check Footer
    removeEmptySettingsOfFooter(config);
}

/**
 * Determines whether a flex change matches the specified template and entity set.
 *
 * The match is performed against the first segment of the `controlId`
 * (before the `--` separator).
 *
 * Instead of comparing the full control ID (for example
 * `appId::template::entitySet`), this function intentionally checks only
 * whether the control ID **ends with** `::template::entitySet`.
 *
 * This is a deliberate simplification to avoid resolving the application ID.
 *
 * @param change - Flex change definition containing the control ID to evaluate.
 * @param template - Template identifier that must be included in the control ID.
 * @param entitySet - Entity set that must be included in the control ID.
 * @returns `true` if the flex change matches the template and entity set; otherwise `false`.
 */
export function flexChangeMatchesTemplateAndEntitySet(
    change: ChangeDefinition,
    template: string,
    entitySet: string
): boolean {
    const controlIdParts = change?.controlId?.split('--') ?? [];
    return controlIdParts[0]?.endsWith(`::${template}::${entitySet}`) ?? false;
}

/**
 * Filters a list of flex change strings by template name and entity set.
 *
 * @param flex - Array of flex change strings to be filtered.
 * @param template - Template identifier that must be included in the control ID.
 * @param entitySet - Entity set that must be included in the control ID.
 * @returns An array of flex change strings matching the given template and entity set.
 */
export function filterFlexChanges(flex: string[], template: string, entitySet: string): string[] {
    return flex.filter((changeStr) => {
        const change = parseChangeString(changeStr) as ChangeDefinition;
        return flexChangeMatchesTemplateAndEntitySet(change, template, entitySet);
    });
}
